import {
    FlightTakeoff,
    DirectionsCar,
    Restaurant,
    Event,
    Star,
    Home,
    DirectionsBoat,
    Celebration,
    Link as LinkIcon,
    Business,
} from '@mui/icons-material';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import { Chip, SvgIcon, SvgIconProps } from '@mui/material';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import React from 'react';
import { ItineraryItemType } from '../../../../enums/itinerary';
import { ItineraryItemInfoDto } from '../../../../types/itinerary';
import {
    serviceTypeColor,
    serviceTypeLeadingIcon,
} from '../../../Contractors/contractorServiceIcons';
import { theme } from '../../../../../shared/theme';
import { ReactComponent as TravelSvg } from './SVG/travel.svg';
import { ReactComponent as DineInSvg } from './SVG/dine_in.svg';

type ItinerarySelectOption = {
    value: any;
    label: React.ReactNode;
    iconLeft?: React.ReactNode;
    iconRight?: React.ReactNode;
    dividerTop?: boolean;
    groupLabel?: string;
    badgeLabel?: string;
    badgeVariant?: 'filled' | 'outlined';
    groupLayout?: 'row' | 'grid';
};

const TravelIcon = (props: SvgIconProps) => (
    <SvgIcon {...props} component={TravelSvg} inheritViewBox />
);

const DineInIcon = (props: SvgIconProps) => (
    <SvgIcon {...props} component={DineInSvg} inheritViewBox />
);

export const ITEM_TYPE_CONFIG: Record<
    string,
    { label: string; icon: React.ComponentType<any>; color: string }
> = {
    [ItineraryItemType.Flight]: {
        label: 'Flight',
        icon: TravelIcon,
        color: theme.palette.itinerary.flight,
    },
    [ItineraryItemType.Transfer]: {
        label: 'Transfer',
        icon: DirectionsCar,
        color: theme.palette.itinerary.transfer,
    },
    [ItineraryItemType.Restaurant]: {
        label: 'Restaurant',
        icon: DineInIcon,
        color: theme.palette.itinerary.restaurant,
    },
    [ItineraryItemType.Event]: {
        label: 'Event',
        icon: Event,
        color: theme.palette.itinerary.event,
    },
    [ItineraryItemType.Custom]: {
        label: 'Custom',
        icon: Star,
        color: theme.palette.itinerary.other,
    },
    [ItineraryItemType.Vehicle]: {
        label: 'Vehicle',
        icon: DirectionsCar,
        color: theme.palette.itinerary.sevenDay,
    },
    [ItineraryItemType.Boat]: {
        label: 'Boat',
        icon: DirectionsBoat,
        color: theme.palette.itinerary.sevenDay,
    },
    [ItineraryItemType.Activity]: {
        label: 'Activity',
        icon: Celebration,
        color: theme.palette.itinerary.sevenDay,
    },
    [ItineraryItemType.Property]: {
        label: 'Linked Booking',
        icon: Home,
        color: theme.palette.itinerary.sevenDay,
    },
    [ItineraryItemType.Commercial]: {
        label: 'Commercial',
        icon: Business,
        color: theme.palette.itinerary.sevenDay,
    },
    [ItineraryItemType.PartnerService]: {
        label: 'Contractor Job',
        icon: Business,
        color: theme.palette.itinerary.sevenDay,
    },
};

export function getItineraryItemIcon(
    info: ItineraryItemInfoDto,
): React.ComponentType<any> {
    if (info.itemType === ItineraryItemType.PartnerService) {
        return serviceTypeLeadingIcon(info.serviceType);
    }

    return ITEM_TYPE_CONFIG[info.itemType].icon;
}

export function getItineraryItemColor(info: ItineraryItemInfoDto): string {
    if (info.itemType === ItineraryItemType.PartnerService) {
        return serviceTypeColor(info.serviceType);
    }

    return ITEM_TYPE_CONFIG[info.itemType].color;
}

export const ItemTypeOptions: ItinerarySelectOption[] = [
    {
        value: ItineraryItemType.Vehicle,
        label: 'Vehicle',
        iconLeft: <DirectionsCar sx={{ fontSize: 18, marginRight: 1 }} />,
        iconRight: (
            <img
                src={theme.palette.custom.lightLogo}
                alt=''
                width={62}
                height={20}
                style={{ objectFit: 'contain' }}
            />
        ),
        groupLabel: 'BOOK VIA 7DAY',
        groupLayout: 'row',
    },
    {
        value: ItineraryItemType.Boat,
        label: 'Boat',
        iconLeft: <DirectionsBoat sx={{ fontSize: 18, marginRight: 1 }} />,
        iconRight: (
            <img
                src={theme.palette.custom.lightLogo}
                alt=''
                width={62}
                height={20}
                style={{ objectFit: 'contain' }}
            />
        ),
        groupLabel: 'BOOK VIA 7DAY',
        groupLayout: 'row',
    },
    {
        value: ItineraryItemType.Activity,
        label: 'Activity',
        iconLeft: <Celebration sx={{ fontSize: 18, marginRight: 1 }} />,
        iconRight: (
            <img
                src={theme.palette.custom.lightLogo}
                alt=''
                width={62}
                height={20}
                style={{ objectFit: 'contain' }}
            />
        ),
        groupLabel: 'BOOK VIA 7DAY',
        groupLayout: 'row',
    },
    {
        value: 'existingReservation',
        label: 'Existing Reservation',
        iconLeft: <LinkIcon sx={{ fontSize: 18, marginRight: 1 }} />,
        groupLabel: 'BOOK VIA 7DAY',
        groupLayout: 'row',
        iconRight: (
            <img
                src={theme.palette.custom.lightLogo}
                alt=''
                width={62}
                height={20}
                style={{ objectFit: 'contain' }}
            />
        ),
    },
    {
        value: ItineraryItemType.PartnerService,
        label: 'Partner Services',
        iconLeft: <LocalActivityIcon sx={{ fontSize: 18, marginRight: 1 }} />,
        groupLabel: 'BOOK VIA 7DAY',
        groupLayout: 'row',
        iconRight: (
            <img
                src={theme.palette.custom.lightLogo}
                alt=''
                width={62}
                height={20}
                style={{ objectFit: 'contain' }}
            />
        ),
    },
    {
        value: ItineraryItemType.Flight,
        label: 'Flight',
        iconLeft: <FlightTakeoff sx={{ fontSize: 18, marginRight: 1 }} />,
        iconRight: (
            <Chip
                label='External'
                variant='outlined'
                size='small'
                sx={{ height: 20, fontSize: 11 }}
            />
        ),
        badgeLabel: 'External',
        badgeVariant: 'outlined',
        groupLabel: 'ADD YOUR OWN',
        groupLayout: 'row',
    },
    {
        value: ItineraryItemType.Transfer,
        label: 'Transfer',
        iconLeft: <AirportShuttleIcon sx={{ fontSize: 18, marginRight: 1 }} />,
        iconRight: (
            <Chip
                label='External'
                variant='outlined'
                size='small'
                sx={{ height: 20, fontSize: 11 }}
            />
        ),
        badgeLabel: 'External',
        badgeVariant: 'outlined',
        groupLabel: 'ADD YOUR OWN',
        groupLayout: 'row',
    },
    {
        value: ItineraryItemType.Restaurant,
        label: 'Restaurant',
        iconLeft: <Restaurant sx={{ fontSize: 18, marginRight: 1 }} />,
        iconRight: (
            <Chip
                label='External'
                variant='outlined'
                size='small'
                sx={{ height: 20, fontSize: 11 }}
            />
        ),
        badgeLabel: 'External',
        badgeVariant: 'outlined',
        groupLabel: 'ADD YOUR OWN',
        groupLayout: 'row',
    },
    {
        value: ItineraryItemType.Event,
        label: 'Event',
        iconLeft: <Event sx={{ fontSize: 18, marginRight: 1 }} />,
        iconRight: (
            <Chip
                label='External'
                variant='outlined'
                size='small'
                sx={{ height: 20, fontSize: 11 }}
            />
        ),
        badgeLabel: 'External',
        badgeVariant: 'outlined',
        groupLabel: 'ADD YOUR OWN',
        groupLayout: 'row',
    },
    {
        value: ItineraryItemType.Custom,
        label: 'Others',
        iconLeft: <Star sx={{ fontSize: 18, marginRight: 1 }} />,
        iconRight: (
            <Chip
                label='External'
                variant='outlined'
                size='small'
                sx={{ height: 20, fontSize: 11 }}
            />
        ),
        badgeLabel: 'External',
        badgeVariant: 'outlined',
        groupLabel: 'ADD YOUR OWN',
        groupLayout: 'row',
    },
];
